import { useState, useSyncExternalStore } from 'react';
import { Printer, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useFeatureToggles } from '@/hooks/useFeatureToggles';
import { printKotWithRetryToast } from '../lib/kotPrintRetry';
import { kotFailures, type KotFailure } from '../lib/printFailureStore';

/**
 * Bandeau persistant des bons cuisine non imprimés (flag `impression_alerte_echec`) :
 * reste affiché tant que le bon n'est pas sorti, avec « Réimprimer » en un geste.
 */
export function PrintFailureBanner() {
  const { isFeatureEnabled } = useFeatureToggles();
  const failures = useSyncExternalStore(kotFailures.subscribe, kotFailures.list);
  const [busy, setBusy] = useState<string | null>(null);

  if (!isFeatureEnabled('impression_alerte_echec') || failures.length === 0) return null;

  const reprint = async (f: KotFailure) => {
    setBusy(f.id);
    await printKotWithRetryToast(f.params);
    setBusy(null);
  };

  return (
    <div
      role="alert"
      className="fixed bottom-4 right-4 z-[60] w-[22rem] max-w-[calc(100vw-2rem)] rounded-lg border-2 border-destructive bg-background shadow-xl"
    >
      <div className="flex items-center gap-2 border-b border-destructive/30 bg-destructive/10 px-3 py-2 font-semibold text-destructive">
        <Printer className="h-4 w-4" />
        {failures.length} bon{failures.length > 1 ? 's' : ''} non imprimé
        {failures.length > 1 ? 's' : ''}
      </div>
      <ul className="max-h-64 divide-y overflow-auto">
        {failures.map((f) => (
          <li key={f.id} className="flex items-center gap-2 px-3 py-2 text-sm">
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">
                Bon {f.label}
                {f.params.payload.tableNumber ? ` · table ${f.params.payload.tableNumber}` : ''}
              </div>
              <div className="text-xs text-muted-foreground">
                {f.params.payload.orderNumber} ·{' '}
                {new Date(f.at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
            <Button size="sm" disabled={busy !== null} onClick={() => void reprint(f)}>
              {busy === f.id ? '…' : 'Réimprimer'}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              aria-label="Ignorer"
              onClick={() => kotFailures.resolve(f.id)}
            >
              <X className="h-4 w-4" />
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
